import type {
  EventRow,
  InspectResult,
  LinkResult,
  NetPolicyRule,
  SeriesPoint,
  Stream,
  StreamDetail,
  Summary,
  SystemPoint,
  Token,
  User,
} from './types';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const err = body as { code?: string; message?: string } | null;
    throw new ApiError(err?.message ?? `Ошибка ${res.status}`, res.status, err?.code ?? 'UNKNOWN');
  }
  return body as T;
}

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}

export const api = {
  login: (login: string, password: string) =>
    request<User>('/api/auth/login', { method: 'POST', body: JSON.stringify({ login, password }) }),
  logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),
  me: () => request<User>('/api/auth/me'),
  health: () => request<{ ok: boolean; node: { id: string; name: string }; mediamtx: { ok: boolean; error?: string } }>('/api/health'),

  tokens: () => request<{ items: Token[] }>('/api/tokens'),
  createToken: (body: Record<string, unknown>) =>
    request<{ token: Token; secret: string }>('/api/tokens', { method: 'POST', body: JSON.stringify(body) }),
  updateToken: (id: string, body: Record<string, unknown>) =>
    request<Token>(`/api/tokens/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  rotateToken: (id: string) =>
    request<{ token: Token; secret: string }>(`/api/tokens/${id}/rotate`, { method: 'POST' }),
  dropPrevSecret: (id: string) => request<Token>(`/api/tokens/${id}/drop-prev-secret`, { method: 'POST' }),
  revokeLinks: (id: string) => request<Token>(`/api/tokens/${id}/revoke-links`, { method: 'POST' }),
  deleteToken: (id: string) => request<{ ok: boolean }>(`/api/tokens/${id}`, { method: 'DELETE' }),

  streams: (tokenId?: string, includeStopped = false) =>
    request<{ items: Stream[] }>(`/api/streams${qs({ tokenId, includeStopped: includeStopped ? 'true' : undefined })}`),
  stream: (key: string) => request<StreamDetail>(`/api/streams/${key}`),
  stopStream: (key: string) => request<{ ok: boolean }>(`/api/streams/${key}/stop`, { method: 'POST' }),
  pinStream: (key: string, pinned: boolean) =>
    request<{ ok: boolean; pinned: boolean }>(`/api/streams/${key}/pin`, {
      method: 'POST',
      body: JSON.stringify({ pinned }),
    }),

  summary: (tokenId?: string) => request<Summary>(`/api/metrics/summary${qs({ tokenId })}`),
  series: (from: number, to: number, tokenId?: string, streamKey?: string) =>
    request<{ points: SeriesPoint[] }>(`/api/metrics/series${qs({ from, to, tokenId, streamKey })}`),
  systemSeries: (from: number, to: number) =>
    request<{ points: SystemPoint[] }>(`/api/metrics/system${qs({ from, to })}`),
  tokenUsage: (from: number, to: number) =>
    request<{ items: Array<{ tokenId: string; sessions: number; streams: number; bytesOut: number }> }>(
      `/api/metrics/tokens${qs({ from, to })}`,
    ),

  events: (params: Record<string, string | number | undefined>) =>
    request<{ items: EventRow[]; total: number }>(`/api/events${qs(params)}`),

  netPolicy: () => request<{ items: NetPolicyRule[] }>('/api/net-policy'),
  addNetPolicy: (body: Record<string, unknown>) =>
    request<{ ok: boolean }>('/api/net-policy', { method: 'POST', body: JSON.stringify(body) }),
  deleteNetPolicy: (id: number) => request<{ ok: boolean }>(`/api/net-policy/${id}`, { method: 'DELETE' }),

  generateLink: (body: Record<string, unknown>) =>
    request<LinkResult>('/api/tools/link', { method: 'POST', body: JSON.stringify(body) }),
  inspect: (body: Record<string, unknown>) =>
    request<InspectResult>('/api/tools/inspect', { method: 'POST', body: JSON.stringify(body) }),
  testSource: (tokenId: string) =>
    request<LinkResult & { src: string }>('/api/tools/test-source', {
      method: 'POST',
      body: JSON.stringify({ tokenId }),
    }),
  envelopeSpec: () => request<Record<string, unknown>>('/api/tools/envelope-spec'),
};
